/****************
 * PUSH ON THE DESKTOP WEB APP: subscribing this browser. 2026-10-21.
 * The worker is public/sw.js (push only). Same server routes as the phone's
 * /m (rest/push_subscriptions.go), which verify the Clerk session.
 ****************/
import i18n from '@/i18n';

const API = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

export function pushSupported(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

function keyBytes(b64: string): Uint8Array {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

async function registration(): Promise<ServiceWorkerRegistration> {
  return (await navigator.serviceWorker.getRegistration('/')) ?? navigator.serviceWorker.register('/sw.js', { scope: '/' });
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration('/');
  return reg ? reg.pushManager.getSubscription() : null;
}

/** Asks the browser, then hands the subscription to the server. */
export async function enablePush(token: string): Promise<'on' | 'denied' | 'unavailable'> {
  if (!pushSupported()) return 'unavailable';
  const keyRes = await fetch(`${API}/api/v1/push/key`).then((r) => r.json()).catch(() => null);
  if (!keyRes?.enabled || !keyRes.public_key) return 'unavailable';
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return 'denied';
  const reg = await registration();
  await navigator.serviceWorker.ready;
  const sub = (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(keyRes.public_key) }));
  const body = { ...sub.toJSON(), lang: i18n.language };
  const res = await fetch(`${API}/api/v1/push/subscriptions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  return res.ok ? 'on' : 'unavailable';
}

export async function disablePush(token: string): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  await fetch(`${API}/api/v1/push/subscriptions`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ endpoint: sub.endpoint }),
  }).catch(() => {});
  await sub.unsubscribe().catch(() => {});
}
